/**
 * Diálogos da página de Contas, com a cara do canvas "Kontto Web — Contas": Transferir, Ajustar
 * saldo (as três naturezas do app, ADR 0074), Nova/Editar conta (com "Fora do saldo total" e
 * Arquivar) e Nova caixinha. Cada um grava pelo AccountsEditor e devolve a mensagem do toast.
 */
import { useState, type ReactNode } from 'react';
import type { Model } from '../viewer/lib/model';
import type { AccountRow } from '../viewer/lib/types';
import type { AccountsEditor, AdjustNature } from '../viewer/lib/editing';
import { reservedOf } from '../viewer/lib/accounts';
import { brl } from '../viewer/lib/format';
import { InstitutionLogo } from '../viewer/components/InstitutionLogo';
import { fmtAmount, parseAmount, parseDay, parseSigned } from '../viewer/components/sheet';
import { Modal, field, label, today } from './ui';

export const ACCOUNT_TYPES: { value: string; label: string }[] = [
  { value: 'checking', label: 'Conta corrente' },
  { value: 'savings', label: 'Poupança' },
  { value: 'wallet', label: 'Carteira (dinheiro)' },
  { value: 'digital', label: 'Conta digital' },
  { value: 'investment', label: 'Investimento' },
  { value: 'other', label: 'Outra' },
];

export type Done = (msg: string, undo?: () => Promise<void>) => void;

export function Chips({ options, value, onPick, testId }: { options: AccountRow[]; value: string; onPick: (id: string) => void; testId: string }) {
  return (
    <div className="acc-chips">
      {options.map(a => (
        <button key={a.id} type="button" aria-pressed={value === a.id} className={`acc-chip ${value === a.id ? 'is-on' : ''}`} onClick={() => onPick(a.id)} data-testid={`${testId}-option`} data-value={a.id}>
          <InstitutionLogo icon={a.institutionIcon} institution={a.institution} name={a.name} color={a.color} size={24} />
          <span>{a.name}</span>
        </button>
      ))}
    </div>
  );
}

export function Foot({ busy, submitLabel, cancelLabel = 'Cancelar', submitDisabled, onCancel, onSubmit, left, error }: { busy: boolean; submitLabel: string; cancelLabel?: string; submitDisabled?: boolean; onCancel: () => void; onSubmit: () => void; left?: ReactNode; error?: string }) {
  return (
    <>
      {error && <p role="alert" className="acc-dlg-error" data-testid="dialog-error">{error}</p>}
      <div className="acc-dlg-foot">
        {left}
        <button type="button" className="acc-btn" onClick={onCancel} disabled={busy}>{cancelLabel}</button>
        <button type="button" className="acc-btn is-primary" onClick={onSubmit} disabled={busy || submitDisabled} data-testid="dialog-submit">{busy ? 'Salvando…' : submitLabel}</button>
      </div>
    </>
  );
}

export function useBusy() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setError('');
    try { await fn(); } catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
  };
  return { busy, error, setError, run };
}

/** Transferir desta conta pra outra. */
export function TransferDialog({ model, account, available, editor, defaultDate, onClose, onDone }: {
  model: Model; account: AccountRow; available: number; editor: AccountsEditor; defaultDate: string; onClose: () => void; onDone: Done;
}) {
  const [dest, setDest] = useState('');
  const [valor, setValor] = useState('');
  const [date, setDate] = useState(defaultDate.slice(8, 10) + '/' + defaultDate.slice(5, 7) + '/' + defaultDate.slice(0, 4));
  const [desc, setDesc] = useState('');
  const [paid, setPaid] = useState(defaultDate <= today());
  const { busy, error, setError, run } = useBusy();
  const dests = model.accounts.filter(a => a.id !== account.id);
  const submit = () => {
    const amount = parseAmount(valor);
    if (amount === null) return setError('Informe o valor da transferência.');
    if (!dest) return setError('Escolha a conta de destino.');
    const iso = parseDay(date, defaultDate);
    if (!iso) return setError('Data inválida (use dd/mm).');
    const to = dests.find(a => a.id === dest)!;
    void run(async () => {
      const id = await editor.transfer({ sourceAccountId: account.id, destinationAccountId: dest, amount, date: iso, description: desc, isConfirmed: paid });
      onDone(`Transferência de ${brl(amount)} para ${to.name} registrada`, () => editor.removeCreated([id]));
      onClose();
    });
  };
  return (
    <Modal title="Nova transferência" onClose={onClose} testId="transfer-dialog">
      <div className="acc-dlg">
        <div className="acc-dlg-group">
          <span className="acc-dlg-label">De</span>
          <div className="acc-from">
            <InstitutionLogo icon={account.institutionIcon} institution={account.institution} name={account.name} color={account.color} size={24} />
            <strong>{account.name}</strong>
            <span className="tnum">Disponível {brl(available)}</span>
          </div>
        </div>
        <div className="acc-dlg-group">
          <span className="acc-dlg-label">Para</span>
          {dests.length ? <Chips options={dests} value={dest} onPick={setDest} testId="transfer-dest" /> : <span className="acc-dlg-note">Crie outra conta pra transferir.</span>}
        </div>
        <div className="acc-dlg-row">
          <label className="acc-dlg-group is-grow"><span className={label}>Valor (R$)</span><input className={field} inputMode="decimal" placeholder="0,00" value={valor} onChange={e => setValor(e.target.value)} autoFocus data-testid="transfer-amount" /></label>
          <label className="acc-dlg-group"><span className={label}>Data</span><input className={`${field} tnum`} placeholder="dd/mm" value={date} onChange={e => setDate(e.target.value)} data-testid="transfer-date" /></label>
        </div>
        <label className="acc-dlg-group"><span className={label}>Descrição (opcional)</span><input className={field} placeholder="Ex.: Guardar pra viagem" value={desc} onChange={e => setDesc(e.target.value)} /></label>
        <label className="acc-check"><input type="checkbox" checked={paid} onChange={e => setPaid(e.target.checked)} />Já transferi</label>
        <Foot busy={busy} error={error} submitLabel="Transferir" onCancel={onClose} onSubmit={submit} />
      </div>
    </Modal>
  );
}

/** Ajustar saldo: mexe no disponível; caixinhas ficam intactas (ADR 0074). */
export function AdjustDialog({ model, account, available, editor, onClose, onDone }: {
  model: Model; account: AccountRow; available: number; editor: AccountsEditor; onClose: () => void; onDone: Done;
}) {
  const reserved = reservedOf(model.reservas, account.id);
  const [novo, setNovo] = useState(fmtAmount(available));
  const [nature, setNature] = useState<AdjustNature>('entry');
  const [dest, setDest] = useState('');
  const { busy, error, setError, run } = useBusy();
  const parsed = parseSigned(novo);
  const diff = parsed === null ? 0 : Math.round((parsed - available) * 100) / 100;
  const dests = model.accounts.filter(a => a.id !== account.id);
  const natures: { id: AdjustNature; title: string; desc: string }[] = [
    { id: 'entry', title: diff < 0 ? 'Lançar como despesa' : 'Lançar como receita', desc: diff < 0 ? 'Gasto não lançado — conta nos relatórios' : 'Entrada não lançada — conta nos relatórios' },
    { id: 'transfer', title: 'Foi transferência', desc: diff < 0 ? 'O valor foi para outra conta sua' : 'O valor veio de outra conta sua' },
    { id: 'neutral', title: 'Apenas ajustar', desc: 'Corrige o saldo sem entrar nos relatórios' },
  ];
  const submitLabel = diff === 0 ? 'Fechar' : nature === 'transfer' ? 'Registrar transferência' : nature === 'neutral' ? 'Ajustar saldo' : diff < 0 ? 'Lançar despesa e ajustar' : 'Lançar receita e ajustar';
  const submit = () => {
    if (parsed === null) return setError('Informe o saldo real de hoje.');
    if (diff === 0) return onClose();
    if (nature === 'transfer' && !dest) return setError('Escolha a outra conta.');
    void run(async () => {
      const ids = await editor.adjust({ accountId: account.id, nature, difference: diff, newBalance: parsed + reserved, counterpartId: dest || undefined, date: today() });
      onDone(`Saldo de ${account.name} ajustado para ${brl(parsed)}`, () => editor.removeCreated(ids));
      onClose();
    });
  };
  return (
    <Modal title={`Ajustar saldo de ${account.name}`} onClose={onClose} testId="adjust-dialog">
      <div className="acc-dlg">
        <span className="acc-dlg-note tnum">Hoje: {brl(available)}{reserved > 0 ? ` · Caixinhas: ${brl(reserved)} (intactas)` : ''}</span>
        <label className="acc-dlg-group"><span className={label}>{reserved > 0 ? 'Saldo disponível (novo)' : 'Saldo real (novo)'}</span><input className={`${field} acc-big tnum`} inputMode="decimal" value={novo} onChange={e => setNovo(e.target.value)} autoFocus data-testid="adjust-new" /></label>
        {diff !== 0 ? (
          <div className="acc-dlg-group">
            <strong className="tnum">{diff < 0 ? 'Pra onde foram ' : 'De onde vieram '}{brl(Math.abs(diff))}?</strong>
            <div className="acc-natures">
              {natures.map(n => (
                <button key={n.id} type="button" aria-pressed={nature === n.id} className={`acc-nature ${nature === n.id ? 'is-on' : ''}`} onClick={() => setNature(n.id)} data-testid={`adjust-${n.id}`}>
                  <strong>{n.title}</strong><span>{n.desc}</span>
                </button>
              ))}
            </div>
            {nature === 'transfer' && (
              <div className="acc-dlg-group">
                <span className="acc-dlg-label">{diff < 0 ? 'Para qual conta' : 'De qual conta'}</span>
                <Chips options={dests} value={dest} onPick={setDest} testId="adjust-dest" />
              </div>
            )}
          </div>
        ) : (
          <span className="acc-dlg-note">O saldo já bate com o do app. Digite o valor que aparece no banco para ajustar.</span>
        )}
        <Foot busy={busy} error={error} submitLabel={submitLabel} onCancel={onClose} onSubmit={submit} />
      </div>
    </Modal>
  );
}

/** Nova conta ou editar a selecionada (nome, tipo, saldo inicial, fora do total, arquivar). */
export function AccountDialog({ account, canArchive, editor, onClose, onDone, onCreated }: {
  account?: AccountRow; canArchive: boolean; editor: AccountsEditor; onClose: () => void; onDone: Done; onCreated?: (id: string) => void;
}) {
  const [name, setName] = useState(account?.name ?? '');
  const [type, setType] = useState(account?.type ?? 'checking');
  const [initial, setInitial] = useState(account ? fmtAmount(account.initialBalance) : '');
  const [off, setOff] = useState(!!account?.isExcludedFromTotals);
  const { busy, error, setError, run } = useBusy();
  const submit = () => {
    const n = name.trim();
    if (!n) return setError('Dê um nome à conta.');
    const init = initial.trim() ? parseSigned(initial) : 0;
    if (init === null) return setError('Saldo inicial inválido.');
    void run(async () => {
      if (account) {
        await editor.updateAccount(account.id, { name: n, type, initialBalance: init, isExcludedFromTotals: off });
        onDone(`Conta ${n} atualizada`);
      } else {
        const id = await editor.createAccount({ name: n, type, initialBalance: init, isExcludedFromTotals: off });
        onCreated?.(id);
        onDone(`Conta ${n} criada`);
      }
      onClose();
    });
  };
  const archive = () => {
    if (!account) return;
    void run(async () => {
      await editor.archiveAccount(account.id);
      onDone(`Conta ${account.name} arquivada`, () => editor.unarchiveAccount(account.id));
      onClose();
    });
  };
  return (
    <Modal title={account ? 'Editar conta' : 'Nova conta'} onClose={onClose} testId="account-dialog">
      <div className="acc-dlg">
        <label className="acc-dlg-group"><span className={label}>Nome</span><input className={field} placeholder="Ex.: Nubank" value={name} onChange={e => setName(e.target.value)} autoFocus data-testid="account-name" /></label>
        <div className="acc-dlg-row">
          <label className="acc-dlg-group is-grow"><span className={label}>Tipo</span>
            <select className={field} value={type} onChange={e => setType(e.target.value)} data-testid="account-type">
              {ACCOUNT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </label>
          <label className="acc-dlg-group"><span className={label}>Saldo inicial</span><input className={`${field} tnum`} inputMode="decimal" placeholder="0,00" value={initial} onChange={e => setInitial(e.target.value)} data-testid="account-initial" /></label>
        </div>
        {account && (
          <label className="acc-check is-boxed">
            <input type="checkbox" checked={off} onChange={e => setOff(e.target.checked)} data-testid="account-off" />
            <span><strong>Fora do saldo total</strong><small>A conta continua visível e disponível para transferências, mas o saldo dela não entra nos totais.</small></span>
          </label>
        )}
        <Foot busy={busy} error={error} submitLabel={account ? 'Salvar' : 'Criar conta'} onCancel={onClose} onSubmit={submit}
          left={account && canArchive ? <button type="button" className="acc-btn is-danger" onClick={archive} disabled={busy} data-testid="account-archive">Arquivar conta</button> : undefined} />
      </div>
    </Modal>
  );
}

/** Nova caixinha nesta conta. O valor inicial já está no saldo (o app chama de "Já está no saldo"). */
export function ReservaDialog({ account, available, editor, onClose, onDone }: {
  account: AccountRow; available: number; editor: AccountsEditor; onClose: () => void; onDone: Done;
}) {
  const [name, setName] = useState('');
  const [target, setTarget] = useState('');
  const [deadline, setDeadline] = useState('');
  const [initial, setInitial] = useState('');
  const { busy, error, setError, run } = useBusy();
  const submit = () => {
    const n = name.trim();
    if (!n) return setError('Dê um nome à caixinha.');
    const targetAmount = target.trim() ? parseAmount(target) : null;
    if (target.trim() && targetAmount === null) return setError('Meta inválida.');
    const initialAmount = initial.trim() ? parseAmount(initial) : 0;
    if (initial.trim() && initialAmount === null) return setError('Valor inicial inválido.');
    if ((initialAmount ?? 0) > 0 && (initialAmount ?? 0) > Math.max(0, available)) return setError(`O valor inicial não pode passar do disponível (${brl(Math.max(0, available))}).`);
    const iso = deadline.trim() ? parseDay(deadline, today()) : null;
    if (deadline.trim() && !iso) return setError('Prazo inválido (use dd/mm/aaaa).');
    void run(async () => {
      await editor.createReserva({ accountId: account.id, name: n, targetAmount, deadline: iso, initialAmount: initialAmount ?? 0 });
      onDone(`Caixinha ${n} criada em ${account.name}`);
      onClose();
    });
  };
  return (
    <Modal title={`Nova caixinha em ${account.name}`} onClose={onClose} testId="reserva-dialog">
      <div className="acc-dlg">
        <label className="acc-dlg-group"><span className={label}>Nome</span><input className={field} placeholder="Ex.: Viagem" value={name} onChange={e => setName(e.target.value)} autoFocus data-testid="reserva-name" /></label>
        <div className="acc-dlg-row">
          <label className="acc-dlg-group is-grow"><span className={label}>Meta (opcional)</span><input className={`${field} tnum`} inputMode="decimal" placeholder="0,00" value={target} onChange={e => setTarget(e.target.value)} data-testid="reserva-target" /></label>
          <label className="acc-dlg-group"><span className={label}>Até (opcional)</span><input className={`${field} tnum`} placeholder="dd/mm/aaaa" value={deadline} onChange={e => setDeadline(e.target.value)} /></label>
        </div>
        <label className="acc-dlg-group"><span className={label}>Guardar agora (já está no saldo)</span><input className={`${field} tnum`} inputMode="decimal" placeholder="0,00" value={initial} onChange={e => setInitial(e.target.value)} data-testid="reserva-initial" /></label>
        <span className="acc-dlg-note">Guardar não cria lançamento: só separa do disponível. Disponível hoje: {brl(available)}.</span>
        <Foot busy={busy} error={error} submitLabel="Criar caixinha" onCancel={onClose} onSubmit={submit} />
      </div>
    </Modal>
  );
}
